import { useState } from 'react'
import { Modal, Field } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { eatInstant } from '@/shared/lib/format'
import { useLoad } from '@/features/administration/hooks'
import { EMPTY_UNIT_FILTERS, listUnits } from '@/features/geography/api'
import { createElection, electionTypes, type Election, type ElectionInput } from './api'
import { DatePicker, DateTimePicker } from '@/components/erp/date-picker'

/**
 * Create an election.
 *
 * <p>This is the control the whole turnout module was blocked on: every screen there is
 * scoped to an election, the empty-state told people to "create one from the Elections
 * tab", and that tab was read-only.</p>
 */
export function ElectionFormDialog({
  open, onClose, onCreated,
}: { open: boolean; onClose: () => void; onCreated: (e: Election) => void }) {
  const types = useLoad(electionTypes, [])
  // Only constituencies and counties can scope a by-election; a national election has none.
  const units = useLoad(
  // size is capped at 200 by UnitQueryService — asking for more is a 400.
    () => listUnits({ ...EMPTY_UNIT_FILTERS, type: ['COUNTY', 'CONSTITUENCY'] }, 0, 200), [])

  const [form, setForm] = useState<ElectionInput>(blank())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const set = <K extends keyof ElectionInput>(k: K, v: ElectionInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      onCreated(await createElection({
        ...form,
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        // datetime-local gives a zoneless wall clock; the server field is an Instant.
        // Read as East Africa Time — see eatInstant.
        pollsOpenAt: eatInstant(form.pollsOpenAt) ?? '',
        pollsCloseAt: eatInstant(form.pollsCloseAt) ?? '',
        // Empty strings are not nulls. A blank optional date must not be sent as "".
        scopeUnitId: form.scopeUnitId || null,
        registrationDeadline: form.registrationDeadline || null,
        transferDeadline: form.transferDeadline || null,
        gazetteReference: form.gazetteReference?.trim() || null,
      }))
      setForm(blank())
      onClose()
    } catch (e) {
      // The server's sentence — a duplicate code, an unknown type, a close before open.
      setError(explain(e))
    } finally {
      setSaving(false)
    }
  }

  const incomplete = !form.code.trim() || !form.name.trim() || !form.electionType
    || !form.pollingDate || !form.pollsOpenAt || !form.pollsCloseAt

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New election"
      description="Cells, pledges and polling-day operations are all scoped to one election."
      width="lg"
      footer={(
        <>
          <button type="button" className="erp-btn-ghost h-9" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary h-9"
            disabled={saving || incomplete}
            onClick={() => { void submit() }}
          >
            {saving ? 'Creating…' : 'Create election'}
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        <div className="grid grid-cols-2 gap-3">
          <Field label="Code" required hint="Short and stable, e.g. GE-2027. Cannot be reused.">
            <input className="erp-input h-9 w-full" value={form.code}
              onChange={(e) => set('code', e.target.value)} placeholder="GE-2027" />
          </Field>
          <Field label="Name" required>
            <input className="erp-input h-9 w-full" value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="2027 General Election" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Type" required hint="Served by the API — the same list the write path validates against.">
            <select className="erp-input h-9 w-full" value={form.electionType}
              disabled={types.loading}
              onChange={(e) => set('electionType', e.target.value)}>
              <option value="">Choose…</option>
              {(types.data ?? []).map((t) => <option key={t} value={t}>{label(t)}</option>)}
            </select>
          </Field>
          <Field label="Year" required>
            <input type="number" className="erp-input h-9 w-full" value={form.electionYear}
              onChange={(e) => set('electionYear', Number(e.target.value))} />
          </Field>
        </div>

        <Field label="Polling date" required>
          <DatePicker value={form.pollingDate}
            onChange={(v) => {
              set('pollingDate', v)
              // Kenyan polls run 06:00–17:00 East Africa Time. Prefilled from the date so
              // nobody hand-types two ISO instants, and both stay editable.
              if (v) {
                set('pollsOpenAt', `${v}T06:00`)
                set('pollsCloseAt', `${v}T17:00`)
              }
            }} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Polls open (EAT)" required>
            <DateTimePicker value={form.pollsOpenAt}
              onChange={(v) => set('pollsOpenAt', v)} />
          </Field>
          <Field label="Polls close (EAT)" required>
            <DateTimePicker value={form.pollsCloseAt}
              onChange={(v) => set('pollsCloseAt', v)} />
          </Field>
        </div>

        <Field label="Scope"
          hint="Leave national unless this is a by-election confined to one area.">
          <select className="erp-input h-9 w-full" value={form.scopeUnitId ?? ''}
            onChange={(e) => set('scopeUnitId', e.target.value || null)}>
            <option value="">National — visible to every scope</option>
            {(units.data?.content ?? []).map((u) => (
              <option key={u.id} value={u.id}>{u.name} ({u.unitType})</option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Registration deadline" hint="Gazetted per cycle. Leave blank until it is.">
            <DatePicker value={form.registrationDeadline ?? ''}
              onChange={(v) => set('registrationDeadline', v || null)} />
          </Field>
          <Field label="Transfer deadline">
            <DatePicker value={form.transferDeadline ?? ''}
              onChange={(v) => set('transferDeadline', v || null)} />
          </Field>
        </div>

        <Field label="Gazette reference">
          <input className="erp-input h-9 w-full" value={form.gazetteReference ?? ''}
            onChange={(e) => set('gazetteReference', e.target.value)}
            placeholder="Gazette Notice No. …" />
        </Field>

        <Note>
          It is created in <strong>PLANNED</strong>. Advance it to ORGANISING when cells can
          start forming — the phase gates what each module will let you do.
        </Note>
      </div>
    </Modal>
  )
}

function blank(): ElectionInput {
  return {
    code: '', name: '', electionYear: new Date().getFullYear(), electionType: '',
    pollingDate: '', pollsOpenAt: '', pollsCloseAt: '',
    scopeUnitId: null, registrationDeadline: null, transferDeadline: null,
    gazetteReference: null,
  }
}

/** GENERAL → General. The server owns the code set; this only sentence-cases it. */
function label(code: string): string {
  return code.charAt(0) + code.slice(1).toLowerCase().replace(/_/g, ' ')
}
